import React from 'react';

import { CardType, TaskType } from '../../../graphql/gen/types';
import { AnalyticsView } from '../analytics/AnalyticsView';
import { RightSideDialog } from '../common/RightSideDialog';
import { PostAnalyticsDialog } from '../details/postAnalytics/PostAnalyticsDialog';
import { QuestionAnalyticsDialog } from '../details/postAnalytics/QuestionAnalyticsDialog';
import { CreateTaskDialog } from '../details/task/CreateTaskDialog';
import { DialogState, useDialogState } from '../dialog/useDialogState';
import { CreateFactAnswerDialog } from './answer/CreateFactAnswerDialog';
import { UpdateFactAnswerDialog } from './answer/UpdateFactAnswerDialog';
import { CreateBoardDialog } from './board/CreateBoardDialog';
import { CreateBoardFromTemplateDialog } from './board/CreateBoardFromTemplateDialog';
import { EditBoardDialog } from './board/EditBoardDialog';
import { BrowsePlexesDialog } from './browse_plexes/BrowsePlexesDialog';
import { CreatePlexDialog } from './create_plex/CreatePlexDialog';
import { EditPlexDialog } from './create_plex/EditPlexDialog';
import { CreateResourceReviewProcessDialog } from './fact_review_process/CreateResourceReviewProcessDialog';
import { EditResourceReviewProcessDialogContainer } from './fact_review_process/EditResourceReviewProcessDialog';
import { ImportFactFromGoogleDocs } from './import_gdocs/ImportFactFromGoogleDocs';
import { ImportFaqsFromGoogleDocs } from './import_gdocs/ImportFaqsFromGoogleDocs';
import { ImportPostFromGoogleDocs } from './import_gdocs/ImportPostFromGoogleDocs';
import { SyncBoardFromGoogleDocsDialog } from './import_gdocs/SyncBoardFromGoogleDocsDialog';
import { SyncPostFromGoogleDocsDialog } from './import_gdocs/SyncPostFromGoogleDocsDialog';
import { ImportToTimelineDialog } from './ImportToTimelineDialog';
import { CreatePostDialog } from './post/CreatePostDialog';
import { UpdatePostDialog } from './post/UpdatePostDialog';
import { CreateFactDialogContent } from './question/CreateFactDialog';
import { FactFromCommentDialog } from './question/FactFromCommentDialog';
import { UpdateFactDialogContent } from './question/UpdateFactDialog';

const DIALOGS_WITH_CLOSE_CONFIRMATION: DialogState['type'][] = [
  'new_fact',
  'edit_fact',
  'new_card',
  'edit_card',
  'new_fact_answer',
  'edit_fact_answer',
  'new_post',
  'import_gdocs_post',
  'edit_post',
  'edit_event',
  'new_event',
  'new_plex',
  'edit_plex',
  'new_task',
  'new_milestone',
  'new_resource_review_process',
];

export function DialogContainer(): React.ReactElement {
  const [dialogState, setDialogState] = useDialogState();

  const open = dialogState != null;

  const handleClose = (): void => {
    setDialogState(null);
  };

  const shouldConfirmClose = dialogState?.type != null && DIALOGS_WITH_CLOSE_CONFIRMATION.includes(dialogState?.type);

  return (
    <RightSideDialog
      key={JSON.stringify(dialogState)}
      open={open}
      onClose={handleClose}
      shouldConfirmClose={shouldConfirmClose}
    >
      {dialogState?.type === 'new_fact' && (
        <CreateFactDialogContent boardId={dialogState.boardId} type={CardType.Faq} />
      )}
      {dialogState?.type === 'new_card' && (
        <CreateFactDialogContent boardId={dialogState.boardId} type={CardType.Generic} />
      )}
      {dialogState?.type === 'edit_fact' && <UpdateFactDialogContent factId={dialogState.factId} />}
      {dialogState?.type === 'edit_card' && <UpdateFactDialogContent factId={dialogState.factId} />}
      {dialogState?.type === 'new_fact_from_comment' && <FactFromCommentDialog commentId={dialogState.commentId} />}
      {dialogState?.type === 'new_fact_answer' && <CreateFactAnswerDialog factId={dialogState.factId} />}
      {dialogState?.type === 'edit_fact_answer' && <UpdateFactAnswerDialog factAnswerId={dialogState.factAnswerId} />}
      {dialogState?.type === 'new_post' && !!dialogState.plexId && (
        <CreatePostDialog type="post" plexId={dialogState.plexId} />
      )}
      {dialogState?.type === 'new_event' && !!dialogState.plexId && (
        <CreatePostDialog type="event" plexId={dialogState.plexId} />
      )}
      {dialogState?.type === 'new_board' && !!dialogState.plexId && <CreateBoardDialog plexId={dialogState.plexId} />}
      {dialogState?.type === 'edit_board' && !!dialogState.boardId && <EditBoardDialog boardId={dialogState.boardId} />}
      {dialogState?.type === 'edit_post' && <UpdatePostDialog postId={dialogState.postId} />}
      {dialogState?.type === 'edit_event' && <UpdatePostDialog postId={dialogState.postId} />}
      {dialogState?.type === 'new_plex' && <CreatePlexDialog />}
      {dialogState?.type === 'edit_plex' && <EditPlexDialog plexId={dialogState.plexId} />}
      {dialogState?.type === 'browse_plexes' && <BrowsePlexesDialog />}
      {dialogState?.type === 'import_gdocs_post' && <ImportPostFromGoogleDocs plexId={dialogState.plexId} />}
      {dialogState?.type === 'import_gdocs_fact' && <ImportFactFromGoogleDocs boardId={dialogState.boardId} />}
      {dialogState?.type === 'sync_gdocs_post' && <SyncPostFromGoogleDocsDialog postId={dialogState.postId} />}
      {dialogState?.type === 'import_gdocs_faqs' && <ImportFaqsFromGoogleDocs plexId={dialogState.plexId} />}
      {dialogState?.type === 'post_analytics_from_post' && <PostAnalyticsDialog postId={dialogState.postId} />}
      {dialogState?.type === 'post_analytics_from_plex' && <PostAnalyticsDialog postId={dialogState.postId} />}
      {dialogState?.type === 'fact_analytics_from_fact' && <QuestionAnalyticsDialog questionId={dialogState.factId} />}
      {dialogState?.type === 'fact_analytics_from_plex' && <QuestionAnalyticsDialog questionId={dialogState.factId} />}
      {dialogState?.type === 'plex_analytics' && <AnalyticsView plexId={dialogState.plexId} />}
      {dialogState?.type === 'import_to_timeline' && <ImportToTimelineDialog plexId={dialogState.plexId} />}
      {dialogState?.type === 'new_board_from_template' && <CreateBoardFromTemplateDialog plexId={dialogState.plexId} />}
      {dialogState?.type === 'sync_gdocs_board' && <SyncBoardFromGoogleDocsDialog boardId={dialogState.boardId} />}
      {dialogState?.type === 'new_task' && <CreateTaskDialog plexId={dialogState.plexId} type={TaskType.Task} />}
      {dialogState?.type === 'new_milestone' && (
        <CreateTaskDialog plexId={dialogState.plexId} type={TaskType.Milestone} />
      )}
      {dialogState?.type === 'new_resource_review_process' && (
        <CreateResourceReviewProcessDialog plexId={dialogState.plexId} />
      )}
      {dialogState?.type === 'edit_resource_review_process' && (
        <EditResourceReviewProcessDialogContainer factReviewProcessId={dialogState.id} />
      )}
    </RightSideDialog>
  );
}
